// src/services/razorpayService.js
// Frontend Razorpay integration
// KEY_ID only — KEY_SECRET never touches the frontend

const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID

// Supabase Edge Function URLs — set VITE_SUPABASE_URL in .env.local
const SUPABASE_URL     = import.meta.env.VITE_SUPABASE_URL
const CREATE_ORDER_URL = `${SUPABASE_URL}/functions/v1/create-order`
const VERIFY_URL       = `${SUPABASE_URL}/functions/v1/verify-payment`
/** Get auth headers with current session token */
async function authHeaders() {
  const { createClient } = await import('@supabase/supabase-js')
  const sb = createClient(SUPABASE_URL, import.meta.env.VITE_SUPABASE_ANON_KEY)
  const { data: { session } } = await sb.auth.getSession()
  const token = session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY
  return {
    'Content-Type':  'application/json',
    'Authorization': `Bearer ${token}`,
  }
}
/**
 * Dynamically load the Razorpay checkout.js SDK.
 * Safe to call multiple times — checks if already loaded.
 */
function loadRazorpaySDK() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(true); return }
    const script    = document.createElement('script')
    script.src      = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload   = () => resolve(true)
    script.onerror  = () => reject(new Error('Failed to load Razorpay SDK'))
    document.head.appendChild(script)
  })
}

/**
 * Step 1 — Call our Edge Function to create a Razorpay order.
 * Returns { order_id, amount, currency } or throws.
 */
async function createOrder(amountInPaise, planId) {
  const res = await fetch(CREATE_ORDER_URL, {
    method:  'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      amount:   amountInPaise,
      currency: 'INR',
      planId,
    }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Order creation failed' }))
    throw new Error(err.error || 'Failed to create payment order')
  }

  return res.json()
}

/**
 * Step 3 — Verify payment signature via our Edge Function.
 * Returns { success, plan, expiresAt } or throws.
 */
async function verifyPayment({ razorpay_order_id, razorpay_payment_id, razorpay_signature, planId, amount, userId }) {
  const res = await fetch(VERIFY_URL, {
    method:  'POST',
    headers:await authHeaders(),
    body: JSON.stringify({
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      planId,
      amount,
      userId,
    }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Verification failed' }))
    throw new Error(err.error || 'Payment verification failed')
  }

  return res.json()
}

/**
 * ── MAIN EXPORT ──────────────────────────────────────────────────
 * startRazorpayCheckout(options)
 *
 * Orchestrates the full flow:
 *   1. Load SDK
 *   2. Create server-side order
 *   3. Open Razorpay modal
 *   4. On payment success → verify signature
 *   5. Call onSuccess(verifyResult) or onFailure(error)
 *
 * @param {object} options
 * @param {string} options.planId        — 'pro' | 'premium' | 'enterprise'
 * @param {number} options.amountInPaise — e.g. 49900 for ₹499
 * @param {string} options.planName      — display name shown in modal
 * @param {string} options.userId        — current user's Supabase UUID
 * @param {string} options.userEmail     — prefilled in Razorpay form
 * @param {string} options.userName      — prefilled in Razorpay form
 * @param {function} options.onSuccess   — called with verifyResult on success
 * @param {function} options.onFailure   — called with Error on failure
 * @param {function} options.onDismiss   — called if user closes modal without paying
 */
export async function startRazorpayCheckout({
  planId,
  amountInPaise,
  planName,
  userId,
  userEmail = '',
  userName  = '',
  onSuccess,
  onFailure,
  onDismiss,
}) {
  try {
    // 1. Load the SDK
    await loadRazorpaySDK()

    // 2. Create server-side order
    const order = await createOrder(amountInPaise, planId)

    // 3. Open Razorpay checkout modal
    await new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key:         RAZORPAY_KEY_ID,
        order_id:    order.order_id,
        amount:      order.amount,
        currency:    order.currency,
        name:        'TecFin',
        description: `Upgrade to ${planName}`,
        image:       '/favicon.svg',

        prefill: {
          name:  userName,
          email: userEmail,
        },

        theme: { color: '#1c3a5e' },

        notes: { planId, userId },

        // 4. Payment success → verify signature server-side
        handler: async (response) => {
          try {
            const result = await verifyPayment({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              planId,
              amount: order.amount,
              userId,
            })
            onSuccess?.(result)
            resolve(result)
          } catch (verifyErr) {
            onFailure?.(verifyErr)
            reject(verifyErr)
          }
        },

        modal: {
          ondismiss: () => {
            onDismiss?.()
            resolve(null)  // not an error, user just closed
          },
          escape:         true,
          backdropclose:  false,
          animation:      true,
        },
      })

      // Handle payment.failed event
      rzp.on('payment.failed', (response) => {
        const err = new Error(
          response.error?.description || 'Payment failed. Please try again.'
        )
        err.code   = response.error?.code
        err.reason = response.error?.reason
        onFailure?.(err)
        reject(err)
      })

      rzp.open()
    })

  } catch (err) {
    onFailure?.(err)
  }
}
